<div *transloco="let t">
	<!-- Reusable template for status cards (chips) -->
	<ng-template #statusCard let-label="label" let-icon="icon" let-isAccent="isAccent" let-items="items">
		<div
			class="inline-flex items-center rounded-lg border overflow-hidden min-h-[2.5rem] shadow-sm"
			[class]="isAccent ? 'border-accent/20 bg-accent/5' : 'border-gray-200 bg-white'">
			<!-- Header Area on the left -->
			<div
				class="px-2 py-1 text-[10px] font-bold border-r self-stretch flex items-center uppercase tracking-tight"
				[class]="isAccent ? 'bg-accent/10 text-accent border-accent/20' : 'bg-gray-100 text-gray-500 border-gray-200 min-w-[32px] justify-center'">
				@if (icon) {
					<span class="material-symbols-outlined">{{ icon }}</span>
				} @else {
					<span class="truncate">{{ label }}</span>
				}
			</div>

			<!-- Content Area on the right -->
			<div class="px-2 py-0.5 flex flex-col justify-center text-[11px] leading-tight min-w-[100px] font-semibold">
				@for (item of items; track item) {
					<div class="flex items-center text-gray-600">
						@if (items.length > 1) {
							<span class="w-1 h-1 rounded-full bg-gray-400 mr-1.5 flex-shrink-0"></span>
						}
						<span [innerHTML]="item"></span>
					</div>
				}
			</div>
		</div>
	</ng-template>

	<!-- Reusable template for sum display (MD/HV) -->
	<ng-template #sumDisplay let-sums="sums" let-icon="icon" let-label="label" let-colorClass="colorClass">
		<div class="flex flex-row items-center gap-2">
			@if (sums.length > 1) {
				<div class="flex flex-col items-center justify-center text-xs" [class]="colorClass">
					<mat-icon class="!text-base">{{ icon }}</mat-icon>
					<span class="text-[10px]">{{ label }}</span>
				</div>
			} @else {
				<div class="flex flex-row items-center gap-1 text-xs" [class]="colorClass">
					<mat-icon class="!text-base">{{ icon }}</mat-icon>
					<span class="text-[10px]">{{ label }}</span>
				</div>
			}
			<div class="flex flex-col justify-center text-xs text-gray-600">
				@for (sum of sums; track sum) {
					<span>
						<span class="font-bold text-sm text-gray-700">Σ</span>
						{{ sum }}
					</span>
				}
				@if (sums.length === 0) {
					<span class="text-gray-400 italic">{{ t('common.noData') }}</span>
				}
			</div>
		</div>
	</ng-template>

	<!-- Reusable template for delivery values display -->
	<ng-template #deliveryDisplay let-deliveries="deliveries" let-label="label">
		<div class="flex flex-row items-center gap-2">
			@if (deliveries.length > 1) {
				<div class="flex flex-col items-center justify-center text-xs text-orange-500">
					<mat-icon class="!text-base">mode_heat</mat-icon>
					<span class="text-[10px]">{{ label }}</span>
				</div>
			} @else {
				<div class="flex flex-row items-center gap-1 text-xs text-orange-500">
					<mat-icon class="!text-base">mode_heat</mat-icon>
					<span class="text-[10px]">{{ label }}</span>
				</div>
			}
			<div class="flex flex-col justify-center text-xs text-gray-600">
				@for (delivery of deliveries; track delivery) {
					<span>{{ delivery }}</span>
				}
			</div>
		</div>
	</ng-template>

	<!-- Reusable template for historical comparison -->
	<ng-template #historyComparison let-comparison="comparison" let-compact="compact">
		@if (comparison) {
			<div class="flex items-center gap-1.5" [class]="compact ? 'text-xs' : ''">
				<mat-icon class="!text-base cursor-help text-gray-500" [matTooltip]="t('billing.historicalComparisonTooltip')">history</mat-icon>
				<span class="text-gray-500">{{ formatPreviousPeriodDates() }}:</span>
				<span class="text-gray-700 font-medium" [class]="compact ? '' : 'whitespace-nowrap'">
					{{ formatComparisonAmount(comparison.previousAmountValues) }}
				</span>
				<span [ngClass]="getComparisonColorClass(comparison.color)" [class]="compact ? '' : 'text-xs font-semibold'">
					@if (comparison.isInfinite) {
						∞
					} @else {
						{{ comparison.deviationPercent > 0 ? '+' : '' }}{{ comparison.deviationPercent.toFixed(0) }}%
					}
				</span>
				<mat-icon [ngClass]="getComparisonColorClass(comparison.color)" class="!text-base">
					{{ comparison.arrowIcon }}
				</mat-icon>
			</div>
		} @else {
			<div class="flex items-center gap-1.5 text-xs text-gray-400">
				<mat-icon class="!text-base" [matTooltip]="t('billing.historicalComparisonUnavailable')">history</mat-icon>
				@if (compact) {
					<span>{{ t('billing.noHistoryData') }}</span>
				}
			</div>
		}
	</ng-template>

	<div class="px-1 mb-2 relative">
		@if (isLoading()) {
			<div class="absolute inset-0 z-10 flex items-center justify-center bg-white/50 rounded-lg">
				<mat-progress-spinner mode="indeterminate" diameter="40" color="primary"></mat-progress-spinner>
			</div>
		}

		@if (flatratesExemptionsService.showDetail$ | async) {
			<vex-flatrates-exemptions
				[billingId]="billingId()"
				[periodId]="period().exportId"
				[costGroup]="costGroup()"
				[showFlatratesExemptionsOfCostGroup]="showFlatratesExemptionsOfCostGroup()"></vex-flatrates-exemptions>
		} @else {
			<ng-template *ngTemplateOutlet="costGroupTemplate; context: { $implicit: costGroup() }"></ng-template>
		}
	</div>

	<ng-template #costGroupTemplate let-currentCostGroup>
		<mat-expansion-panel [expanded]="isExpanded" (afterExpand)="isExpanded = true" (afterCollapse)="isExpanded = false">
			<mat-expansion-panel-header style="padding-right: 1rem !important">
				<mat-panel-title class="flex flex-1 flex-row justify-between items-center w-full">
					<div class="flex flex-1 justify-between">
						<div class="flex flex-row items-center min-w-44 justify-start">
							<div class="flex-row min-w-0 ml-2 overflow-hidden">
								<div class="truncate">
									<span
										[ngClass]="{ 'text-red-600 font-semibold': !currentCostGroup.name }"
										class="text-lg cursor-pointer hover:text-primary hover:underline transition-colors"
										(click)="$event.stopPropagation(); editCostGroup(currentCostGroup)">
										{{ currentCostGroup.name || t('enum.UNKNOWN') }}
									</span>
									<span class="font-medium text-xs text-primary flex-shrink-0">({{ getInvoiceCount(currentCostGroup) }})</span>
								</div>

								@if (currentCostGroup.costType) {
									<div class="truncate text-gray-600 flex flex-row gap-3 text-xs">
										<span>
											<span>{{ t('billing.costTypes.' + currentCostGroup.costType) }}</span>
										</span>
									</div>
								}

								@if (currentCostGroup.printname && currentCostGroup.description) {
									<div class="truncate text-sm text-gray-600 flex flex-row gap-3">
										<div>{{ currentCostGroup.printname || '–' }},</div>
										<ara-popover class="inline-flex align-middle" [content]="currentCostGroup.description">
											<div class="text-ellipsis max-w-xs overflow-hidden">
												{{ currentCostGroup.description || '–' }}
											</div>
										</ara-popover>
									</div>
								}
							</div>
						</div>

						<!-- Summen: In Tabellenansicht Chips, in Kartenansicht (eingeklappt) neues Design -->
						@if (
							viewMode() === 'card' &&
							(costGroupType().category === CostGroupCategory.HEAT_ENERGY ||
								costGroupType().category === CostGroupCategory.HEATING_OPERATION ||
								costGroupType().category === CostGroupCategory.ANCILLARY_SERVICES) &&
							!isExpanded
						) {
							<!-- Neues Design für Kartenansicht (eingeklappt) -->
							<div class="flex flex-row items-center gap-6 ml-8">
								@if (currentCostGroup.invoiceCategories.length === 1) {
									<ng-container
										*ngTemplateOutlet="
											sumDisplay;
											context: {
												sums: getInvoiceSums(currentCostGroup, false),
												icon: 'business',
												label: t('common.serviceCompany_short'),
												colorClass: 'text-gray-500',
											}
										"></ng-container>

									<div class="w-px h-8 bg-gray-200"></div>

									<ng-container
										*ngTemplateOutlet="
											sumDisplay;
											context: {
												sums: getInvoiceSums(currentCostGroup, true),
												icon: 'apartment',
												label: t('common.propertyManagement_short'),
												colorClass: 'text-gray-500',
											}
										"></ng-container>
								}

								@if (getCurrentDeliveryTranslations(currentCostGroup).length) {
									@if (currentCostGroup.invoiceCategories.length === 1) {
										<div class="w-px h-8 bg-gray-200"></div>
									}
									<ng-container
										*ngTemplateOutlet="
											deliveryDisplay;
											context: {
												deliveries: getDeliveryValuesSum(currentCostGroup),
												label: t('billing.deliveryValues'),
											}
										"></ng-container>
								}
							</div>
						} @else if (
							viewMode() !== 'card' ||
							(costGroupType().category !== CostGroupCategory.HEAT_ENERGY &&
								costGroupType().category !== CostGroupCategory.HEATING_OPERATION &&
								costGroupType().category !== CostGroupCategory.ANCILLARY_SERVICES)
						) {
							<!-- Chip-Design für Tabellenansicht -->
							<div class="flex flex-row flex-wrap flex-1 items-center gap-2 ml-8 text-sm text-gray-600">
								<ng-container
									*ngTemplateOutlet="
										statusCard;
										context: {
											label: t('common.serviceCompany_short'),
											items: getInvoiceSums(currentCostGroup, false, true),
										}
									"></ng-container>

								<ng-container
									*ngTemplateOutlet="
										statusCard;
										context: {
											label: t('common.propertyManagement_short'),
											items: getInvoiceSums(currentCostGroup, true, true),
										}
									"></ng-container>

								@if (getCurrentDeliveryTranslations(currentCostGroup).length) {
									<ng-container
										*ngTemplateOutlet="
											statusCard;
											context: {
												icon: 'mode_heat',
												isAccent: true,
												items: getDeliveryValuesSum(currentCostGroup),
											}
										"></ng-container>
								}
							</div>
						}
					</div>
					<div class="text-right flex flex-row items-center flex-shrink-0">
						<button mat-icon-button [matTooltip]="t('billing.exemption')" (click)="$event.stopPropagation(); openExemptions(currentCostGroup)">
							<mat-icon matBadge="{{ exemptionCount() }}">person_off</mat-icon>
						</button>

						<button mat-icon-button [matTooltip]="t('billing.flatrate')" (click)="$event.stopPropagation(); openFlatrates(currentCostGroup)">
							<mat-icon matBadge="{{ flatrateCount() }}">euro_symbol</mat-icon>
						</button>

						@if (editableAllocationCostGroup() && editableAllocationCostGroup() === currentCostGroup) {
							<div class="flex flex-row items-center" (click)="$event.stopPropagation()">
								@if (availableAllocations().length > 0) {
									<div
										class="select-container w-[280px] pt-2 truncate transition-all duration-300"
										[class.allocation-auto-selected]="allocationAutoSelected()"
										style="margin-bottom: -14px">
										<mat-form-field appearance="outline" class="w-full">
											<mat-label>{{ t('billing.allocationSelect') }}</mat-label>
											<mat-select [(ngModel)]="selectedNewAllocation" panelClass="custom-select-options">
												<mat-select-trigger class="allocationSelectTrigger">
													{{ getAllocationName(selectedNewAllocation()) }}
													@if (selectedNewAllocation()) {
														<mat-icon
															class="mat-select-clear-icon"
															[matTooltip]="t('commonActions.deleteSelection')"
															(click)="$event.stopPropagation(); selectedNewAllocation.set(undefined)">
															clear
														</mat-icon>
													}
												</mat-select-trigger>
												@for (currentAllocation of availableAllocations(); track currentAllocation.exportId) {
													<mat-option [value]="currentAllocation">
														<vex-allocation-option [allocation]="currentAllocation" />
													</mat-option>
												}
											</mat-select>
										</mat-form-field>
									</div>
								}
								<button mat-icon-button (click)="saveAllocation()" [matTooltip]="t('commonActions.cancel')">
									<mat-icon>cancel</mat-icon>
								</button>
								<button
									mat-icon-button
									type="submit"
									[matTooltip]="t('billing.addCostAllocation')"
									(click)="addCostAllocation(currentCostGroup)">
									<mat-icon>add_circle</mat-icon>
								</button>
								@if (availableAllocations().length > 0) {
									<button
										mat-icon-button
										[disabled]="!hasAllocationChanged()"
										(click)="saveAllocation(currentCostGroup)"
										[matTooltip]="t('commonActions.save')">
										<mat-icon>save</mat-icon>
									</button>
								}
							</div>
						} @else {
							<div class="flex items-center gap-1 mx-1" (click)="$event.stopPropagation()">
								<div class="flex flex-col">
									<span class="text-xs text-gray-500">{{ t('billing.allocation') }}</span>
									@if (allocationExists()) {
										<div class="flex items-center gap-1">
											<span class="text-sm font-medium">
												{{ getAllocationName(currentAllocation()) }}
											</span>
											@if (!period().locked) {
												<button
													mat-icon-button
													class="!w-6 !h-6 !min-w-0 !p-0 !leading-none"
													(click)="editAllocation(currentCostGroup)">
													<mat-icon class="!text-[16px] !w-4 !h-4" color="primary">edit</mat-icon>
												</button>
											}
										</div>
									} @else {
										@if (!period().locked) {
											<a class="text-sm text-primary cursor-pointer hover:underline" (click)="editAllocation(currentCostGroup)">
												{{ t('billing.allocationNotSet') }}
											</a>
										} @else {
											<span class="text-sm text-gray-400 italic">{{ t('billing.allocationNotSet') }}</span>
										}
									}
								</div>
							</div>
						}

						<div class="h-12 w-px bg-gray-300 mx-1"></div>
						<button mat-icon-button [matMenuTriggerFor]="costGroupActions" [matTooltip]="t('billing.costGroup')" (click)="$event.stopPropagation()">
							<mat-icon>more_vert</mat-icon>
						</button>
					</div>
				</mat-panel-title>
			</mat-expansion-panel-header>

			<!-- Card View -->
			@if (
				viewMode() === 'card' &&
				(costGroupType().category === CostGroupCategory.HEAT_ENERGY ||
					costGroupType().category === CostGroupCategory.HEATING_OPERATION ||
					costGroupType().category === CostGroupCategory.ANCILLARY_SERVICES)
			) {
				<!-- Trennlinie unter Header in Primary-Farbe (volle Breite) -->
				<div class="h-px mb-3 -mx-6" style="background-color: rgba(var(--color-primary), 1)"></div>

				@for (category of currentCostGroup.invoiceCategories; track category.exportId) {
					@let invoices = getCategoryInvoicesDataSource(invoiceCategoryInvoiceDataSource, category);
					<div class="mb-4">
						<!-- Kategorie-Überschrift mit History -->
						<div class="flex items-center justify-between mb-2">
							<div class="text-sm font-bold text-gray-700">{{ t('enum.' + category.type) }}</div>
							<ng-container
								*ngTemplateOutlet="historyComparison; context: { comparison: getHistoricalComparison(category), compact: true }"></ng-container>
						</div>

						<div class="flex flex-wrap gap-3">
							@for (invoice of invoices.data; track invoice.exportId) {
								<div
									class="invoice-card flex flex-col border rounded-lg shadow-sm transition-shadow w-[300px]"
									[class.cursor-pointer]="!period().locked && isCustomerInvoice(invoice)"
									[class.bg-white]="isCustomerInvoice(invoice)"
									[class.border-gray-200]="isCustomerInvoice(invoice)"
									[class.hover:shadow-md]="isCustomerInvoice(invoice)"
									[class.bg-gray-50]="!isCustomerInvoice(invoice)"
									[class.border-gray-300]="!isCustomerInvoice(invoice)"
									[class.opacity-60]="!isCustomerInvoice(invoice)"
									(click)="isCustomerInvoice(invoice) ? toInvoice(invoice, category) : null">
									<!-- Oberste Zeile: Name + Datum + MD/HV Badge + 3-Punkt-Menü -->
									<div class="flex items-start justify-between p-3 pb-1 border-b border-gray-100">
										<div class="flex-1 min-w-0 pr-2">
											<div class="flex items-center gap-1.5">
												@if (isCustomerInvoice(invoice)) {
													<span
														class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary/10 text-primary flex-shrink-0">
														HV
													</span>
												} @else {
													<span
														class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-200 text-gray-600 flex-shrink-0">
														MD
													</span>
												}
												<span class="font-medium text-sm truncate" [title]="invoice.name">
													{{ invoice.name || t('common.unnamed') }}
												</span>
											</div>
											<!-- Datum unter dem Namen -->
											@if (invoice.invoiceDate?.length) {
												<div class="flex items-center text-xs text-gray-500 mt-0.5">
													<mat-icon class="!text-sm mr-1">calendar_today</mat-icon>
													{{ invoice.invoiceDate | date: 'dd.MM.yyyy' }}
												</div>
											} @else {
												<div class="flex items-center text-xs text-gray-400 italic mt-0.5">
													<mat-icon class="!text-sm mr-1">calendar_today</mat-icon>
													{{ t('billing.noDate') }}
												</div>
											}
										</div>
										@if (!period().locked && isCustomerInvoice(invoice)) {
											<button
												mat-icon-button
												class="!w-8 !h-8 !min-w-[32px] !p-0 flex-shrink-0"
												[matMenuTriggerFor]="cardInvoiceActions"
												(click)="$event.stopPropagation()">
												<mat-icon class="text-gray-500 !text-xl">more_vert</mat-icon>
											</button>
											<mat-menu #cardInvoiceActions="matMenu">
												<button mat-menu-item (click)="toInvoice(invoice, category)">
													<mat-icon color="primary">edit</mat-icon>
													<div>{{ t('commonActions.edit') }}</div>
												</button>
												<button mat-menu-item (click)="deleteInvoice(invoice)">
													<mat-icon color="primary">delete</mat-icon>
													<div>{{ t('commonActions.delete') }}</div>
												</button>
											</mat-menu>
										}
									</div>

									<!-- Chips für Rechnungsinfos -->
									<div class="flex flex-col gap-1.5 px-3 py-2">
										@if (!isInvoiceCategoryRemainingStock(invoice)) {
											<ng-container
												*ngTemplateOutlet="
													statusCard;
													context: {
														label: isCustomerInvoice(invoice)
															? t('common.propertyManagement_short')
															: t('common.serviceCompany_short'),
														items: getInvoiceAmountValues(currentCostGroup, invoice),
													}
												"></ng-container>
										}

										@if (invoice.invoiceDeliveryValues?.length) {
											<ng-container
												*ngTemplateOutlet="
													statusCard;
													context: {
														icon: 'mode_heat',
														isAccent: true,
														items: getDeliveryValues(invoice),
													}
												"></ng-container>
										}
									</div>
								</div>
							}

							<!-- Add Button pro Kategorie -->
							@if (!period().locked) {
								<button
									class="add-invoice-btn flex flex-col items-center justify-center gap-2 px-6 py-4 rounded-lg transition-all"
									[disabled]="isInvoiceCategoryDisabled(category)"
									(click)="newInvoice(category)">
									<mat-icon class="text-primary">add_circle_outline</mat-icon>
									<span class="text-sm font-medium">{{ t('billing.newInvoice') }}</span>
								</button>
							}
						</div>
					</div>
				}

				<!-- Fußzeile: Summen -->
				<div class="flex items-center mt-4 pt-3 border-t border-gray-200">
					<div class="flex flex-row items-center gap-6">
						@if (currentCostGroup.invoiceCategories.length === 1) {
							<ng-container
								*ngTemplateOutlet="
									sumDisplay;
									context: {
										sums: getInvoiceSums(currentCostGroup, false),
										icon: 'business',
										label: t('common.serviceCompany_short'),
										colorClass: 'text-gray-500',
									}
								"></ng-container>

							<div class="w-px h-8 bg-gray-200"></div>

							<ng-container
								*ngTemplateOutlet="
									sumDisplay;
									context: {
										sums: getInvoiceSums(currentCostGroup, true),
										icon: 'apartment',
										label: t('common.propertyManagement_short'),
										colorClass: 'text-gray-500',
									}
								"></ng-container>
						}

						@if (getCurrentDeliveryTranslations(currentCostGroup).length) {
							@if (currentCostGroup.invoiceCategories.length === 1) {
								<div class="w-px h-8 bg-gray-200"></div>
							}
							<ng-container
								*ngTemplateOutlet="
									deliveryDisplay;
									context: {
										deliveries: getDeliveryValuesSum(currentCostGroup),
										label: t('billing.deliveryValues'),
									}
								"></ng-container>
						}
					</div>
				</div>
			} @else {
				<!-- Table View (Original mit Category-Box) -->
				@for (category of currentCostGroup.invoiceCategories; track category.exportId) {
					<div class="flex flex-col border border-gray-200 rounded-xl p-2 mb-2">
						<div class="flex flex-row justify-between">
							<div class="min-w-44 flex-shrink-0 text-base font-bold">
								{{ t('enum.' + category.type) }}
							</div>

							<div class="ml-auto flex items-center gap-1">
								<ng-container
									*ngTemplateOutlet="
										historyComparison;
										context: { comparison: getHistoricalComparison(category), compact: false }
									"></ng-container>
							</div>
						</div>
						@let invoices = getCategoryInvoicesDataSource(invoiceCategoryInvoiceDataSource, category);

						<!-- Table View -->
						<table
							@stagger
							[dataSource]="invoices"
							class="flex-1 min-w-0 items-center hover-rows rounded-xl"
							mat-table
							matSort
							matSortDirection="asc">
							<ng-container matColumnDef="summary">
								<td *matCellDef="let invoice" mat-cell class="flex-1">
									<div class="flex flex-row flex-1 items-center min-w-0">
										<!-- Zeile 1 -->
										<div class="min-w-44 flex-shrink-0 truncate">
											<div class="text-xs">
												{{ invoice.name }}
											</div>
											@if (invoice.invoiceDate?.length) {
												<div class="font-light text-xs text-gray-500">({{ invoice.invoiceDate | date: 'dd.MM.yyyy' }})</div>
											}
										</div>

										<div class="flex flex-row flex-wrap items-center ml-2 gap-2 text-sm text-gray-600">
											@if (!isInvoiceCategoryRemainingStock(invoice)) {
												<ng-container
													*ngTemplateOutlet="
														statusCard;
														context: {
															label: isCustomerInvoice(invoice)
																? t('common.propertyManagement_short')
																: t('common.serviceCompany_short'),
															items: getInvoiceAmountValues(currentCostGroup, invoice),
														}
													"></ng-container>
											}

											@if (invoice.invoiceDeliveryValues?.length) {
												<ng-container
													*ngTemplateOutlet="
														statusCard;
														context: {
															icon: 'mode_heat',
															isAccent: true,
															items: getDeliveryValues(invoice),
														}
													"></ng-container>
											}
										</div>
									</div>
								</td>
							</ng-container>

							<ng-container matColumnDef="actions">
								<td *matCellDef="let invoice" mat-cell class="w-12 text-right">
									<div class="flex items-center justify-end gap-1">
										@if (isCustomerInvoice(invoice)) {
											<span class="chip chip--hv" [matTooltip]="t('billing.billingFromPropertyManagement')">HV</span>
										} @else {
											<span class="chip chip--md" [matTooltip]="t('billing.billingFromServiceCompany')">MD</span>
										}
										@if (!period().locked && isCustomerInvoice(invoice)) {
											<button mat-icon-button [matMenuTriggerFor]="invoiceActions" (click)="$event.stopPropagation()">
												<mat-icon>more_vert</mat-icon>
											</button>
											<mat-menu #invoiceActions="matMenu">
												<button mat-menu-item (click)="toInvoice(invoice, category)">
													<mat-icon color="primary">edit</mat-icon>
													<div>{{ t('commonActions.edit') }}</div>
												</button>
												<button mat-menu-item (click)="deleteInvoice(invoice)">
													<mat-icon color="primary">delete</mat-icon>
													<div>{{ t('commonActions.delete') }}</div>
												</button>
											</mat-menu>
										}
									</div>
								</td>
							</ng-container>

							<tr
								(click)="isCustomerInvoice(invoice) ? toInvoice(invoice, category) : null"
								*matRowDef="let invoice; columns: displayedCategoryColumns"
								@fadeInUp
								class="trans-ease-out"
								[class]="
									(!period().locked && isCustomerInvoice(invoice) ? 'cursor-pointer ' : '') +
									(!isCustomerInvoice(invoice) ? 'opacity-60' : '')
								"
								mat-row></tr>

							@if (invoices.data.length === 0) {
								<tr class="mat-row">
									<td class="mat-cell" [attr.colspan]="displayedCategoryColumns.length">
										<div class="ml-4">{{ t('dashboard.noDataFound') }}</div>
									</td>
								</tr>
							}
						</table>
						@if (
							(costGroupType().category === CostGroupCategory.HEAT_ENERGY ||
								costGroupType().category === CostGroupCategory.HEATING_OPERATION ||
								costGroupType().category === CostGroupCategory.ANCILLARY_SERVICES) &&
							!period().locked
						) {
							<div class="flex flex-row justify-center">
								<button (click)="newInvoice(category)" mat-raised-button [disabled]="isInvoiceCategoryDisabled(category)">
									<mat-icon color="primary">add</mat-icon>
									<span>
										{{ t('enum.' + category.type) }}
									</span>
								</button>
							</div>
						}
					</div>
				}
			}
		</mat-expansion-panel>
		<mat-menu #costGroupActions="matMenu">
			@if (!period().locked) {
				<button mat-menu-item (click)="openAssignAllocationDialog(currentCostGroup)">
					<mat-icon color="primary" fontSet="material-symbols-outlined">graph_4</mat-icon>
					<div>{{ t('billing.addAllocation') }}</div>
				</button>
				<hr />
			}

			<button mat-menu-item (click)="editCostGroup(currentCostGroup)">
				<mat-icon color="primary">edit</mat-icon>
				<div>{{ t('commonActions.edit') }}</div>
			</button>
			@if (!period().locked) {
				<button mat-menu-item (click)="deleteCostGroup(currentCostGroup)">
					<mat-icon color="primary">delete</mat-icon>
					<div>{{ t('commonActions.delete') }}</div>
				</button>
			}
		</mat-menu>
	</ng-template>
</div>
